import React from "react";
import { ClientErrorRecoveryView } from "./ClientErrorRecoveryView";
import { createClientErrorId } from "./clientErrorIds";
import type { ClientErrorRecoveryLabels, ClientErrorReporter } from "./clientErrorTypes";
import { buildReportedStack, formatErrorCause, readErrorRequestId, toReportableError } from "./clientErrorTypes";
import { readModuleHint } from "./clientErrorReportText";
import { getCurrentPath } from "./currentPath";
import { isTemporaryApiUnavailableError } from "../api/createApiRequest";
import { signalAppRuntimeUnavailable } from "../system/appRuntimeGate";
import { isApiRequestAborted } from "../api/apiRequestGate";

// language-neutral: controlled-values Standard browser ResizeObserver notification messages.
const RESIZE_OBSERVER_DEFERRED_MESSAGE = "ResizeObserver loop completed with undelivered notifications.";
const RESIZE_OBSERVER_LIMIT_MESSAGE = "ResizeObserver loop limit exceeded";

export const globalClientErrorEventName = "mqorva-global-error";

export type GlobalClientErrorDetail = {
  colno?: number;
  error?: Error;
  errorId?: string;
  lineno?: number;
  message?: string;
  reason?: unknown;
  source?: string;
  type: "error" | "unhandledrejection";
};

const getErrorFromDetail = (detail: GlobalClientErrorDetail): Error => {
  if (detail.reason instanceof Error) return detail.reason;
  if (detail.error instanceof Error) return detail.error;
  return toReportableError(detail.reason ?? detail.message ?? "Unknown error");
};

const getErrorSource = (detail: GlobalClientErrorDetail, error: Error): string | null => {
  if (detail.source && detail.lineno) {
    const base = detail.source.split("/").pop() || detail.source;
    return `${base}:${detail.lineno}${detail.colno ? `:${detail.colno}` : ""}`;
  }

  const stack = error.stack ?? "";
  const match = stack.match(/([^/\s()]+\.(?:tsx?|jsx?|js)):(\d+):(\d+)/);
  return match ? `${match[1]}:${match[2]}:${match[3]}` : null;
};

export function dispatchGlobalClientError(detail: GlobalClientErrorDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(globalClientErrorEventName, { detail }));
}

let installed = false;

export type GlobalClientErrorHandlerOptions = {
  /**
   * Fachlich erwartete Zustände des Produkts (z. B. gesperrter Mandant, abgelehnte Formularprüfung).
   * `true` heißt: vom Produkt behandelt — kein Overlay, keine Meldung.
   */
  handleExpected?: (reason: unknown) => boolean;
};

export function installGlobalClientErrorHandlers(reportError: ClientErrorReporter, options: GlobalClientErrorHandlerOptions = {}) {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    // The browser defers resize notifications to the next paint; this is not a thrown
    // application exception. Do not manufacture a crash/stack or a failed API log.
    // language-neutral: controlled-values Standard browser ResizeObserver notification messages.
    const deferredResize = event.message === RESIZE_OBSERVER_DEFERRED_MESSAGE
      || event.message === RESIZE_OBSERVER_LIMIT_MESSAGE;
    if (deferredResize && event.error == null && !event.filename && event.lineno === 0 && event.colno === 0) {
      return;
    }
    if (options.handleExpected?.(event.error ?? event.message)) {
      event.preventDefault();
      return;
    }
    const errorId = createClientErrorId("ERR");
    const error = toReportableError(event.error ?? event.message);
    dispatchGlobalClientError({
      colno: event.colno,
      error,
      errorId,
      lineno: event.lineno,
      message: event.message,
      source: event.filename,
      type: "error",
    });
    reportError({
      errorId,
      message: error.message,
      sourceKind: "Unhandled",
      stack: buildReportedStack(error),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isApiRequestAborted(event.reason)) { event.preventDefault(); return; }
    if (isTemporaryApiUnavailableError(event.reason)) {
      event.preventDefault();
      signalAppRuntimeUnavailable("updating");
      return;
    }
    if (options.handleExpected?.(event.reason)) {
      event.preventDefault();
      return;
    }

    const errorId = createClientErrorId("ERR");
    const error = toReportableError(event.reason);
    dispatchGlobalClientError({ errorId, reason: event.reason, type: "unhandledrejection" });
    reportError({
      errorId,
      message: error.message,
      sourceKind: "Unhandled",
      stack: buildReportedStack(error),
    });
  });
}

type GlobalClientErrorOverlayProps = {
  labels: ClientErrorRecoveryLabels;
  showDeveloperDetails?: boolean;
  onBackToDashboard: () => void;
  onReload?: () => void;
};

export function GlobalClientErrorOverlay({
  labels,
  showDeveloperDetails,
  onBackToDashboard,
  onReload,
}: GlobalClientErrorOverlayProps) {
  const [state, setState] = React.useState<{ detail: GlobalClientErrorDetail; error: Error; errorId: string } | null>(null);

  React.useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<GlobalClientErrorDetail>).detail;
      if (!detail) return;
      const error = getErrorFromDetail(detail);
      setState({ detail, error, errorId: detail.errorId ?? createClientErrorId("ERR") });
    };

    window.addEventListener(globalClientErrorEventName, handler);
    return () => window.removeEventListener(globalClientErrorEventName, handler);
  }, []);

  if (!state) return null;

  const errorLocation = getErrorSource(state.detail, state.error);
  const reportInput = {
    componentStack: null,
    currentPath: getCurrentPath(),
    errorCause: formatErrorCause(state.error),
    errorId: state.errorId,
    errorLocation,
    errorMessage: state.error.message,
    moduleHint: readModuleHint(errorLocation),
    requestId: readErrorRequestId(state.error),
    stack: state.error.stack ?? null,
  };

  return (
    <ClientErrorRecoveryView
      errorMessage={state.error.message}
      labels={labels}
      mode="overlay"
      reportInput={reportInput}
      showDeveloperDetails={showDeveloperDetails}
      stack={state.error.stack ?? null}
      onBackToDashboard={() => {
        setState(null);
        onBackToDashboard();
      }}
      onClose={() => setState(null)}
      onReload={onReload}
    />
  );
}
